import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Pagination,
  Select,
  Table,
  TableColumnsType,
} from "antd";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectViewShowBy } from "../../constants/selectViewShowBy";
import { IListClientTable } from "../../interfaces/ListTable/IListClientTable";
import { useArchiveClientMutation } from "../../store/api/listClient.api";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import {
  SELECT_CLIENT,
  SELECT_CLIENT_BY_ID,
  SELECT_CLIENT_FORM_TYPE,
  SELECT_LIST_CLIENT_ACTIVE,
  SELECT_LIST_CLIENT_ARCHIVE,
} from "../../store/selectors/listClientSelector";
import {
  CHANGE_CLIENT_FORM_DISABLE,
  CHANGE_CLIENT_FORM_INITIAL,
  CHANGE_CLIENT_FORM_TYPE,
  CHANGE_SELECTED_CLIENT,
  CLIENT_ARCHIVE,
  IClient,
} from "../../store/slice/listClientSlice/listClientSlice";
import { UI_ITOModuleTitle } from "../../UI_ITO/indexUI";

import classes from "./PageListClient.module.css";

/**
 * Компонент страницы списка клиентов.
 */
export function PageListClient(): ReactElement {
  const { id } = useParams();
  const findClient = useAppSelector(SELECT_CLIENT_BY_ID(id ? +id : undefined));
  const dispatch = useAppDispatch();
  const listClientActive = useAppSelector(SELECT_LIST_CLIENT_ACTIVE);
  const listClientArchive = useAppSelector(SELECT_LIST_CLIENT_ARCHIVE);
  const selectedClient = useAppSelector(SELECT_CLIENT);
  const formType = useAppSelector(SELECT_CLIENT_FORM_TYPE);
  const [isModalMoveToArchiveOpen, setIsModalMoveToArchiveOpen] =
    useState(false);
  const [filteredListClientActive, setFilteredListClientActive] =
    useState<IClient[]>(listClientActive);
  const [filteredListClientArchive, setFilteredListClientArchive] =
    useState<IClient[]>(listClientArchive);
  const [isOrganisationContained, setIsOrganisationContained] = useState(
    Boolean(selectedClient?.organization_name),
  );

  const [archiveClient, { isError: archiveClientError }] =
    useArchiveClientMutation();

  function changeSelectedClient(client: IClient) {
    dispatch(CHANGE_CLIENT_FORM_INITIAL(client));
    dispatch(CHANGE_SELECTED_CLIENT(client));
    dispatch(CHANGE_CLIENT_FORM_DISABLE(true));
    dispatch(CHANGE_CLIENT_FORM_TYPE("INFO"));
    if (client?.organization) {
      setIsOrganisationContained(true);
    } else {
      setIsOrganisationContained(false);
    }
  }

  const navigate = useNavigate();

  useEffect(() => {
    if (listClientActive.length !== 0) {
      setFilteredListClientActive(listClientActive);
      if (findClient) {
        changeSelectedClient(findClient);
      }
    }
    setFilteredListClientArchive(listClientArchive);
  }, [listClientActive, listClientArchive]);

  const columnsApplication: TableColumnsType<IListClientTable> = [
    {
      title: "Наименование организации",
      dataIndex: "organizationName",
      render: (organizationName) => (
        <span
          style={{ color: "#3c79ff", cursor: "pointer" }}
          onClick={() => {
            navigate(`/client/${organizationName.id}`);
          }}
        >
          {organizationName.name}
        </span>
      ),
    },
    {
      title: "ФИО директора",
      dataIndex: "fullNameDirector",
    },
    {
      title: "ИНН клиента",
      dataIndex: "clientInn",
    },
    {
      title: "ОГРН",
      dataIndex: "organizationOgrn",
    },
    {
      title: "ФИО контактного лица",
      dataIndex: "fullNameClient",
    },
    {
      title: "Контактный номер ",
      dataIndex: "contactNumber",
    },
  ];

  const dataTableClientSource: IListClientTable[] =
    filteredListClientActive?.map((item: IClient) => {
      const { id, organization, inn, surname, name, patronymic, phone_number } =
        item;

      return {
        organizationName: { name: organization?.name || "", id },
        fullNameDirector: `${surname} ${name} ${patronymic}` || "",
        organizationInn: organization?.inn || "",
        clientInn: inn,
        organizationOgrn: organization?.ogrn || "",
        // TODO: Пока с бэка не приходит, заменить как пофиксят бэк
        fullNameClient: "",
        contactNumber: phone_number || "",
        id,
      };
    });

  function editClientInfo() {
    dispatch(CHANGE_CLIENT_FORM_DISABLE(false));
    dispatch(CHANGE_CLIENT_FORM_TYPE("EDIT"));
  }

  function handleSearch(value: string) {
    setFilteredListClientActive(() => {
      if (value) {
        return listClientActive.filter(
          (client: IClient) =>
            client?.name?.toLowerCase().includes(value.toLowerCase()) ||
            client?.surname?.toLowerCase().includes(value.toLowerCase()) ||
            client?.patronymic?.toLowerCase().includes(value.toLowerCase()) ||
            String(client?.inn).includes(value),
        );
      } else {
        return listClientActive;
      }
    });
    setFilteredListClientArchive(() => {
      if (value) {
        return listClientArchive.filter(
          (client: IClient) =>
            client?.name?.toLowerCase().includes(value.toLowerCase()) ||
            client?.surname?.toLowerCase().includes(value.toLowerCase()) ||
            client?.patronymic?.toLowerCase().includes(value.toLowerCase()) ||
            String(client?.inn).includes(value),
        );
      } else {
        return listClientArchive;
      }
    });
  }

  function toggleArchive(id: number, is_enabled: boolean) {
    archiveClient({ client_id: id, is_enabled: !is_enabled });
    if (!archiveClientError) {
      dispatch(CLIENT_ARCHIVE({ client_id: id, is_enabled: !is_enabled }));
    }
  }

  function addToArchive() {
    if (selectedClient?.id && selectedClient?.is_enable) {
      toggleArchive(selectedClient.id, selectedClient.is_enable);
      setIsModalMoveToArchiveOpen(false);
      changeSelectedClient(
        filteredListClientActive?.filter(
          (client: IClient) => client.id !== selectedClient.id,
        )[0],
      );
    }
  }

  function moveToArchive() {
    setIsModalMoveToArchiveOpen(true);
  }

  function handleCancelMoveToArchive() {
    setIsModalMoveToArchiveOpen(false);
  }

  return (
    <>
      <div className={classes.listClient}>
        <div className={classes.listClientPageHeader}>
          <UI_ITOModuleTitle title="Клиенты" />
          <div className={classes.listClientPageFilters}>
            <Input
              className={classes.listClientInput}
              placeholder="ИП, ООО"
              size="large"
              allowClear
              suffix={<SearchOutlined className={classes.listClientIcon} />}
              onClick={(event: any) => handleSearch(event.target.value)}
            />

            <Input
              className={classes.listClientInput}
              placeholder="ИНН"
              size="large"
              allowClear
              suffix={<SearchOutlined className={classes.listClientIcon} />}
              onClick={(event: any) => handleSearch(event.target.value)}
            />

            <Button
              type="primary"
              size="large"
              className={classes.listClientFiltersButton}
            >
              Сбросить фильтр
              <CloseOutlined className={classes.listClientIcon} />
            </Button>
          </div>
        </div>

        <div className={classes.clientListContainer}>
          <Table
            className={classes.listClientTable}
            columns={columnsApplication}
            dataSource={dataTableClientSource}
            pagination={false}
            size="small"
          />
        </div>
        <div className={classes.paginationContainer}>
          <div>
            Показывать по:{" "}
            <Select
              options={selectViewShowBy}
              className={classes.paginationSelect}
            />
          </div>

          <Pagination
            className={classes.pagination}
            total={10}
            defaultPageSize={2}
          />
        </div>
      </div>
    </>
  );
}
